import { Box, FormControlLabel, Grid, Switch } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import {
  useGetMeasureUnitsQuery,
  useGetTemperatureRangesQuery,
} from '../../../../app/redux-fetch/apiCoreQuery'
import { useGetProductCategoriesQuery } from '../../../../app/redux-fetch/apiProducts'
import { useLoggedInUser } from '../../../../app/hooks/useLoggedInUser'
import { convertToLabelValue } from '../../../../app/utils'
import BoxWhiteContainer from '../../../../components/Containers/BoxWhiteContainer'
import CombinedSelect from '../../../../components/Forms/CombinedSelect'
import FormInput from '../../../../components/Forms/FormInput'
import FormSelect from '../../../../components/Forms/FormSelect'
import InfoDisplayItem from '../../../../components/Typographies/InfoDisplayItem'
import { SectionTitleTypography } from '../../../../components/Typographies/styles'
import { messages } from './messages'
import DefaultSpacingGrid from '../../../../components/Containers/DefaultSpacingGrid'
import { IProductCategory } from '../../types'

interface TemperatureProps {
  isDisabled?: boolean
  customerId?: number
  showOverrideToggle?: boolean
}

const Temperature = ({ isDisabled, customerId = -1, showOverrideToggle: showOverrideToggleProp = true }: TemperatureProps) => {
  const { formatMessage } = useIntl()
  const { control, setValue, trigger, getValues } = useFormContext()
  const { tenantId } = useLoggedInUser()

  const { data: temperatureRange = [] } = useGetTemperatureRangesQuery()
  const { data: measureUnits = [] } = useGetMeasureUnitsQuery()
  const { data: productCategories = [] } = useGetProductCategoriesQuery(
    { tenantId: tenantId ?? -1, customerId },
    { skip: customerId < 1 || !tenantId || tenantId < 1 }
  )

  const temperatureSetting = useWatch({ control, name: 'temperatureSetting' })
  const temperatureRangeId = useWatch({ control, name: 'temperatureSetting.temperatureRangeId' })
  const uomId = useWatch({ control, name: 'temperatureSetting.temperatureUnitId' })
  const lowerWarning = useWatch({ control, name: 'temperatureSetting.lowerThresholdWarning' })
  const upperWarning = useWatch({ control, name: 'temperatureSetting.upperThresholdWarning' })
  const lowerCritical = useWatch({ control, name: 'temperatureSetting.lowerThresholdCritical' })
  const upperCritical = useWatch({ control, name: 'temperatureSetting.upperThresholdCritical' })
  const setPoint = useWatch({ control, name: 'temperatureSetting.setPoint' })
  const productCategoryId = useWatch({ control, name: 'productCategoryId' })

  // Track whether the user has toggled override on
  const [isOverridden, setIsOverridden] = useState(false)

  // Track the previous category ID to detect changes
  const prevCategoryIdRef = useRef<number | undefined>(undefined)

  // Determine if the product already has its own saved temperature setting (has an id)
  const productHasOwnTempSetting = !!getValues('temperatureSettingId')

  // Initialize override state: if product already has its own temp setting, start with override on
  const hasInitializedRef = useRef(false)
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
      if (productHasOwnTempSetting) {
        setIsOverridden(true)
      }
    }
  }, [productHasOwnTempSetting])

  // Look up the currently selected category
  const selectedCategory = useMemo(() => {
    if (!productCategoryId || productCategoryId < 1) return undefined
    return (productCategories as IProductCategory[]).find(
      (cat) => cat.id === productCategoryId
    )
  }, [productCategoryId, productCategories])

  // Whether the selected category has temperature settings
  const categoryHasTempSettings = !!selectedCategory?.temperatureSetting

  // Auto-populate temperature settings from category when category changes
  useEffect(() => {
    if (!productCategoryId || productCategoryId < 1) return

    // Wait for categories to load before processing
    if ((productCategories as IProductCategory[]).length === 0) return

    // Only run when category actually changes
    if (prevCategoryIdRef.current === productCategoryId) return
    prevCategoryIdRef.current = productCategoryId

    // Don't auto-populate if product already has its own saved temp setting
    if (productHasOwnTempSetting) return

    // Don't auto-populate if user has overridden
    if (isOverridden) return

    if (selectedCategory?.temperatureSetting) {
      const catTemp = selectedCategory.temperatureSetting
      const options = { shouldDirty: true, shouldValidate: false }
      // Copy temperature settings from category as a template (exclude id)
      setValue('temperatureSetting', {
        temperatureRangeId: catTemp.temperatureRangeId ?? null,
        temperatureUnitId: catTemp.temperatureUnitId ?? null,
        setPoint: catTemp.setPoint ?? '',
        upperThresholdWarning: catTemp.upperThresholdWarning ?? '',
        upperThresholdCritical: catTemp.upperThresholdCritical ?? '',
        lowerThresholdWarning: catTemp.lowerThresholdWarning ?? '',
        lowerThresholdCritical: catTemp.lowerThresholdCritical ?? '',
      }, options)
    } else {
      // Category has no temperature settings, clear any auto-populated values
      const currentTemp = getValues('temperatureSetting')
      if (currentTemp && !getValues('temperatureSettingId')) {
        const options = { shouldDirty: true, shouldValidate: false }
        setValue('temperatureSetting', undefined, options)
        setValue('temperatureSettingId', null, options)
      }
    }
  }, [productCategoryId, productCategories, selectedCategory, productHasOwnTempSetting, isOverridden, setValue, getValues])

  // Determine if temperature is populated from category (not overridden, no own setting, and category actually has temp settings)
  const isFromCategory = !!temperatureSetting && !isOverridden && !productHasOwnTempSetting && categoryHasTempSettings
  const fieldsDisabled = isDisabled || isFromCategory

  const temperatureRangeOptions = useMemo(() => {
    return convertToLabelValue(temperatureRange)
  }, [temperatureRange])

  const temperatureUnitsOptions = useMemo(() => {
    return convertToLabelValue(
      measureUnits
        .filter((x) => x.measureTypeId === 3)
        .map((x) => {
          return { name: x.units, id: x.id }
        })
    )
  }, [measureUnits])

  const getTemperatureUnit = useCallback(
    (id: number) => {
      const result = measureUnits.find((unit) => unit.id === id)?.units
      return result
    },
    [measureUnits]
  )

  const getTemperatureRangeName = useCallback(
    (id: number) => {
      const range = temperatureRange.find((r: any) => r.id === id)
      return range ? range.name : 'N/A'
    },
    [temperatureRange]
  )

  const clearTemperatureSettings = useCallback(() => {
    const options = { shouldDirty: true, shouldValidate: false }
    setValue('temperatureSetting', undefined, options)
    setValue('temperatureSettingId', null, options)
  }, [setValue])

  // Determine if temperature controlled based on presence of temperatureSettings object
  const isTemperatureControlled = !!temperatureSetting

  const handleTemperatureControlledChange = useCallback((checked: boolean) => {
    const options = { shouldDirty: true, shouldValidate: false }
    if (checked) {
      setIsOverridden(true)
      setValue('temperatureSetting', {
        temperatureRangeId: null,
        temperatureUnitId: null,
        setPoint: '',
        upperThresholdWarning: '',
        upperThresholdCritical: '',
        lowerThresholdWarning: '',
        lowerThresholdCritical: '',
      }, options)
    } else {
      setIsOverridden(false)
      clearTemperatureSettings()
    }
  }, [clearTemperatureSettings, setValue])

  const handleOverrideChange = useCallback((checked: boolean) => {
    setIsOverridden(checked)
    const options = { shouldDirty: true, shouldValidate: false }
    if (!checked) {
      // When turning off override, re-populate from category if available
      if (selectedCategory?.temperatureSetting) {
        const catTemp = selectedCategory.temperatureSetting
        setValue('temperatureSetting', {
          temperatureRangeId: catTemp.temperatureRangeId ?? null,
          temperatureUnitId: catTemp.temperatureUnitId ?? null,
          setPoint: catTemp.setPoint ?? '',
          upperThresholdWarning: catTemp.upperThresholdWarning ?? '',
          upperThresholdCritical: catTemp.upperThresholdCritical ?? '',
          lowerThresholdWarning: catTemp.lowerThresholdWarning ?? '',
          lowerThresholdCritical: catTemp.lowerThresholdCritical ?? '',
        }, options)
        // Clear the product's own temperatureSettingId so UI knows it's inherited from category
        setValue('temperatureSettingId', null, options)
      } else {
        // Category has no temperature settings — preserve existing custom values and ID to prevent data loss
      }
    }
  }, [selectedCategory, setValue])

  const handleInputChange = useCallback(async () => {
    await trigger('temperatureSetting.upperThresholdWarning')
    await trigger('temperatureSetting.upperThresholdCritical')
    await trigger('temperatureSetting.lowerThresholdWarning')
    await trigger('temperatureSetting.lowerThresholdCritical')
    await trigger('temperatureSetting.setPoint')
  }, [trigger])

  if (isDisabled) {
    const unitSymbol = getTemperatureUnit(uomId)

    return (
      <BoxWhiteContainer sx={{ width: '100%' }}>
        <Grid container>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <SectionTitleTypography>{formatMessage(messages.temperature)}</SectionTitleTypography>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <InfoDisplayItem
              label={formatMessage(messages.temperatureRange)}
              value={getTemperatureRangeName(temperatureRangeId) || 'N/A'}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <InfoDisplayItem
              label={formatMessage(messages.uom)}
              value={getTemperatureUnit(uomId) || 'N/A'}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <InfoDisplayItem
              label={formatMessage(messages.lowerWarning)}
              value={lowerWarning !== undefined ? `${lowerWarning} ${unitSymbol}` : 'N/A'}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={3}></Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <InfoDisplayItem
              label={formatMessage(messages.upperWarning)}
              value={upperWarning !== undefined ? `${upperWarning} ${unitSymbol}` : 'N/A'}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <InfoDisplayItem
              label={formatMessage(messages.lowerCritical)}
              value={lowerCritical !== undefined ? `${lowerCritical} ${unitSymbol}` : 'N/A'}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <InfoDisplayItem
              label={formatMessage(messages.upperCritical)}
              value={upperCritical !== undefined ? `${upperCritical} ${unitSymbol}` : 'N/A'}
              isSmallScreen={false}
            />
          </Grid>
        </Grid>
      </BoxWhiteContainer>
    )
  }

  // Always show override toggle when temperature controlled - controls whether fields are read-only or editable
  const showOverrideToggle = showOverrideToggleProp && isTemperatureControlled

  return (
    <Box sx={{ width: '100%' }}>
      <FormControlLabel
        control={
          <Switch
            checked={isTemperatureControlled}
            onChange={(e) => handleTemperatureControlledChange(e.target.checked)}
            disabled={isDisabled || (isFromCategory && !isOverridden)}
          />
        }
        label={formatMessage(messages.temperatureControlled)}
        sx={{ mb: 2 }}
      />
      {showOverrideToggle && (
        <FormControlLabel
          control={
            <Switch
              checked={isOverridden}
              onChange={(e) => handleOverrideChange(e.target.checked)}
            />
          }
          label={formatMessage({
            id: 'product.temperature.overrideSettings',
            defaultMessage: 'Override Category Settings',
          })}
          sx={{ mb: 2, ml: 2 }}
        />
      )}
      {isTemperatureControlled && (
        <>
        <SectionTitleTypography>{formatMessage(messages.temperature)}</SectionTitleTypography>
        <DefaultSpacingGrid container spacing={1}>
          <Grid item xs={12} md={2}>
            <FormSelect
              name='temperatureSetting.temperatureRangeId'
              label={formatMessage(messages.temperatureRange)}
              options={temperatureRangeOptions}
              fullWidth
              disabled={fieldsDisabled}
              placeholder={formatMessage(messages.pleaseSelect)}
              required={true}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <CombinedSelect
              nameValue={'temperatureSetting.setPoint'}
              nameUnit='temperatureSetting.temperatureUnitId'
              labelValue={formatMessage(messages.temperatureSetPoint)}
              labelUnitValue={formatMessage(messages.uom)}
              optionsUnit={temperatureUnitsOptions}
              showLabel={false}
              required={true}
              numberFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: true,
                placeholder: '0.00',
              }}
              onChange={handleInputChange}
              disabled={fieldsDisabled}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormInput
              name='temperatureSetting.lowerThresholdCritical'
              label={formatMessage(messages.lowerCritical)}
              InputLabelProps={{ shrink: true }}
              type='number'
              unit={getTemperatureUnit(uomId)}
              fullWidth
              disabled={fieldsDisabled}
              required={true}
              numberFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: true,
              }}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormInput
              name='temperatureSetting.lowerThresholdWarning'
              label={formatMessage(messages.lowerWarning)}
              InputLabelProps={{ shrink: true }}
              type='number'
              unit={getTemperatureUnit(uomId)}
              fullWidth
              disabled={fieldsDisabled}
              numberFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: true,
              }}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormInput
              name='temperatureSetting.upperThresholdWarning'
              label={formatMessage(messages.upperWarning)}
              InputLabelProps={{ shrink: true }}
              type='number'
              unit={getTemperatureUnit(uomId)}
              fullWidth
              disabled={fieldsDisabled}
              numberFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: true,
              }}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormInput
              name='temperatureSetting.upperThresholdCritical'
              label={formatMessage(messages.upperCritical)}
              InputLabelProps={{ shrink: true }}
              type='number'
              unit={getTemperatureUnit(uomId)}
              fullWidth
              disabled={fieldsDisabled}
              required={true}
              numberFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: true,
              }}
              onChange={handleInputChange}
            />
          </Grid>
        </DefaultSpacingGrid>
        </>
      )}
    </Box>
  )
}

export default Temperature
